// src/components/sections/blogs.js

import React, { useState, useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { usePrefersReducedMotion, observeReveal } from '@hooks';
import BlogList from '@components/bloglist';

/* ────────────────  STYLES  ──────────────── */
const StyledBlogsSection = styled.section`
  max-width: 700px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
    margin-bottom: 2rem;
  }

  .archive-link {
    font-family: var(--font-sans);
    font-size: var(--fz-lg);
    &:after {
      bottom: 0.4em;
    }
  }

  .list-wrapper {
    width: 100%;
    margin-top: var(--space-4);
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: var(--space-8) auto 0;
  }
`;

const Blogs = () => {
  const data = useStaticQuery(graphql`
    query {
      blogs: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/blogs/" }
          frontmatter: { showInBlogs: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              date
              title
              tags
              slug
              description
            }
          }
        }
      }

      allBlogs: allMarkdownRemark(filter: { fileAbsolutePath: { regex: "/content/blogs/" } }) {
        totalCount
      }
    }
  `);

  const prefersReducedMotion = usePrefersReducedMotion();
  const titleRef = useRef(null);
  const linkRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }

    const stop = [
      observeReveal(titleRef.current),
      observeReveal(linkRef.current),
      observeReveal(listRef.current, 200),
    ];

    return () => stop.forEach(fn => fn && fn());
  }, [prefersReducedMotion]);

  const LIMIT = 6;
  const blogs = data.blogs.edges.map(e => e.node.frontmatter);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? blogs : blogs.slice(0, LIMIT);

  return (
    <StyledBlogsSection id="blogs">
      <Section>
        <h2 className="numbered-heading" ref={titleRef}>
          I Write about AI, Philosophy, and More
        </h2>

        <Link className="archive-link inline-link" to="/blogs" ref={linkRef}>
          View all blogs ({data.allBlogs.totalCount})
        </Link>

        <div className="list-wrapper" ref={listRef}>
          <BlogList blogs={visible} />
        </div>

        {blogs.length > LIMIT && (
          <button className="more-button" onClick={() => setShowAll(!showAll)}>
            Show {showAll ? 'Less' : 'More'}
          </button>
        )}
      </Section>
    </StyledBlogsSection>
  );
};

export default Blogs;
